import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
} from 'rxjs';
import { TrackEventAction, TrackType } from 'src/app/common/interfaces/rtc-interface';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { SessionsSyncService } from 'src/app/services/sessions-sync.service';
import {
  ISessionViewState,
  ScribbleType,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import {
  ActiveCanvasStateService,
  ActiveCanvasType,
} from 'src/app/services/active-canvas-state.service';
import { YCanvas } from '../wb-canvas/fabric-utils';
import { BottomToolbarType } from '../wb-main-toolbar-v2/utils/bottom-toolbar.types';
import {
  MainToolbarService,
  PenTypeEnum,
} from '../wb-main-toolbar-v2/services/main-toolbar.service';
import {
  FullScreenAppManagerService,
  isFullScreeningAppInCall,
} from './full-screen-app-manager.service';

interface ToolbarState {
  lastSelectedTool: BottomToolbarType;
  lastActivePen: PenTypeEnum;
}

export enum VideoSource {
  SCREEN_SHARE = 'screen_share',
  FULLSCREEN_APP = 'fullscreen_app',
}

export function isSameSessionViewStateForAnnotations(
  prev: ISessionViewState,
  curr: ISessionViewState,
): boolean {
  return (
    prev.view === curr.view &&
    prev.expandedFullScreenChange?.id === curr.expandedFullScreenChange?.id &&
    prev.expandedFullScreenChange?.tileData?.trackType ===
      curr.expandedFullScreenChange?.tileData?.trackType &&
    prev.sessionViewMetaData?.canAnnotate === curr.sessionViewMetaData?.canAnnotate &&
    prev.sessionViewMetaData?.fullscreenId === curr.sessionViewMetaData?.fullscreenId
  );
}

@UntilDestroy()
@Injectable()
export class ScreenShareAnnotationsManagerService {
  private lastLocalScreenShareTrackSid?: string;
  private _currentVideoSource: VideoSource = VideoSource.SCREEN_SHARE;
  private screenShareAnnotationsCanvas: YCanvas | null = null;
  private _toolbarState: ToolbarState = {
    lastSelectedTool: BottomToolbarType.PEN,
    lastActivePen: PenTypeEnum.DRAW,
  };
  private _isToolbarExpanded = new BehaviorSubject<boolean>(false);
  private _isToolbarDisabled = false;
  private _selectedToolbarTool = new BehaviorSubject<BottomToolbarType>(
    this._toolbarState.lastSelectedTool,
  );

  public readonly isToolbarExpanded$ = this._isToolbarExpanded.asObservable();
  public readonly selectedToolbarTool$ = this._selectedToolbarTool
    .asObservable()
    .pipe(distinctUntilChanged());
  public readonly isToolbarDisabled$ = this.mainToolbarService.isSpaceLocked$;

  // to disable annotations for specific apps in call
  public readonly shouldDisableAnnotations$ = this.sharedDataService.sessionView.current$.pipe(
    map((sessionView) => {
      if (
        this.fullScreenAppManagerService.isFullScreenAppInCallEnabled() &&
        sessionView.sessionViewMetaData?.canAnnotate === false
      ) {
        return true;
      }
      return false;
    }),
    distinctUntilChanged(),
  );

  private readonly fullScreenAppStateChanged$ = combineLatest([
    this.fullScreenAppManagerService.appStateChanged$.pipe(startWith(undefined)),
    this.fullScreenAppManagerService.appStateRemoved$.pipe(startWith(undefined)),
  ]).pipe(map(() => void 0));

  private canShowAnnotationsLayerOnTile$ = combineLatest([
    this.sharedDataService.sessionView.current$,
    this.fullScreenAppStateChanged$,
  ]).pipe(
    map(([sessionView]) => this.shouldActivateGalleryCanvas(sessionView)),
    distinctUntilChanged(),
  );

  constructor(
    private sessionsSyncService: SessionsSyncService,
    private providerStateService: ProviderStateService,
    private flagsService: FlagsService,
    private sharedDataService: SessionSharedDataService,
    private activeCanvasStateService: ActiveCanvasStateService,
    private mainToolbarService: MainToolbarService,
    private fullScreenAppManagerService: FullScreenAppManagerService,
  ) {}

  public init(): void {
    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_SCREEN_SHARE_ANNOTATIONS)) {
      this.setupScreenShareAnnotationListeners();
      // this.setupMobileFullscreenToolbarCollapse();
    }

    combineLatest([
      this.sharedDataService.sessionView.current$.pipe(
        distinctUntilChanged(isSameSessionViewStateForAnnotations),
      ),
      this.shouldDisableAnnotations$,
      this.fullScreenAppStateChanged$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([sessionView, shouldDisableAnnotations]) => {
        const shouldActivateGalleryCanvas = this.shouldActivateGalleryCanvas(sessionView);
        this.activeCanvasStateService.setActiveCanvasType(
          shouldActivateGalleryCanvas ? ActiveCanvasType.GALLERY_VIEW : ActiveCanvasType.WHITEBOARD,
        );
        if (shouldActivateGalleryCanvas) {
          this.activeCanvasStateService.setActiveCanvas(this.screenShareAnnotationsCanvas);
          return;
        }
        if (this.sharedDataService.fabricCanvas && !shouldActivateGalleryCanvas) {
          this.activeCanvasStateService.setActiveCanvas(this.sharedDataService.fabricCanvas);
        }
        if (shouldDisableAnnotations || !shouldActivateGalleryCanvas) {
          this.toggleToolbarExpanded(false);
        }
      });

    // To handle the case when the space is locked outside the gallery view
    this.isToolbarDisabled$.pipe(untilDestroyed(this)).subscribe((disbaled) => {
      this._isToolbarDisabled = disbaled;
      if (disbaled) {
        this._toolbarState.lastSelectedTool = BottomToolbarType.EMPTY;
        this._selectedToolbarTool.next(BottomToolbarType.EMPTY);
        this.toggleToolbarExpanded(false);
      } else {
        this.resetToolbarStateIfEmpty();
      }
    });
  }

  private setupScreenShareAnnotationListeners(): void {
    this.providerStateService.trackEvents$
      .pipe(
        untilDestroyed(this),
        filter((event) => !!event),
      )
      .subscribe((trackEvent) => {
        if (!trackEvent || trackEvent.type !== TrackType.SCREEN || !trackEvent.participant.local) {
          return;
        }

        if (trackEvent.action === TrackEventAction.START) {
          const screenShareTrackSid = trackEvent.participant.screenShareTrackSid;
          this.lastLocalScreenShareTrackSid = screenShareTrackSid;
          // Clear any stale annotations from previous shares
          this.clearAnnotationsByTileId(screenShareTrackSid);
          return;
        }

        if (trackEvent.action === TrackEventAction.STOP) {
          this.clearAnnotationsByTileId(trackEvent.participant.screenShareTrackSid);
        }
      });

    this.providerStateService.callConnected$
      .pipe(untilDestroyed(this), pairwise(), distinctUntilChanged())
      .subscribe(([wasConnected, isConnected]) => {
        const syncRoomId = this.activeCanvasStateService.getScreenShareAnnotationsRoomId();
        if (isConnected) {
          // join sync channel for screen share annotations when user joins the call
          this.sessionsSyncService.joinRooms([syncRoomId]);
        }
        if (wasConnected && !isConnected) {
          this.clearAnnotationsByTileId(this.lastLocalScreenShareTrackSid);
          // close sync channel for screen share annotations when user leaves the call
          this.sessionsSyncService.leaveRooms([syncRoomId]);
        }
      });
  }

  private clearAnnotationsByTileId(tileId?: string): void {
    if (!tileId) {
      return;
    }

    this.clearScreenShareTileContent(tileId);
  }

  /**
   * Clear all content (all fabric objects) for a specific tileId
   */
  public clearScreenShareTileContent(tileId: string): void {
    const roomId = this.activeCanvasStateService.getScreenShareAnnotationsRoomId();
    const ySession = this.sessionsSyncService.getYSessionById(roomId);
    if (!ySession) {
      return;
    }

    ySession.clearFrameContent(roomId, tileId);
  }

  public showScreenShareAnnotationsTool$ = combineLatest([
    this.activeCanvasStateService.activeCanvasType$,
    this.providerStateService.callConnected$,
    this.canShowAnnotationsLayerOnTile$,
    this.shouldDisableAnnotations$,
  ]).pipe(
    map(
      ([canvasType, connected, canShowAnnotationsLayerOnTile, shouldDisableAnnotations]) =>
        canvasType === ActiveCanvasType.GALLERY_VIEW &&
        connected &&
        canShowAnnotationsLayerOnTile &&
        !shouldDisableAnnotations,
    ),
    distinctUntilChanged(),
  );

  // we make screen share annotations interactive only when the annotations is enabled and the toolbar is expanded
  public isAnnotationsInteractive$ = combineLatest([
    this.showScreenShareAnnotationsTool$,
    this.isToolbarExpanded$,
  ]).pipe(
    map(
      ([showScreenShareAnnotationsTool, isToolbarExpanded]) =>
        showScreenShareAnnotationsTool && isToolbarExpanded,
    ),
    distinctUntilChanged(),
  );

  public setScreenShareAnnotationsCanvas(canvas: YCanvas | null): void {
    this.screenShareAnnotationsCanvas = canvas;
    if (this.activeCanvasStateService.activeCanvasType === ActiveCanvasType.GALLERY_VIEW) {
      this.activeCanvasStateService.setActiveCanvas(canvas);
    }
  }

  // clear the shared screen-share canvas if it still owns that exact canvas reference
  public clearScreenShareAnnotationsCanvasIfCurrent(canvas: YCanvas | undefined): void {
    if (!canvas || this.screenShareAnnotationsCanvas !== canvas) {
      return;
    }

    this.screenShareAnnotationsCanvas = null;
    if (
      this.activeCanvasStateService.activeCanvasType === ActiveCanvasType.GALLERY_VIEW &&
      this.activeCanvasStateService.activeCanvas === canvas
    ) {
      this.activeCanvasStateService.setActiveCanvas(null);
    }
  }

  private canShowAnnotationsLayerOnFullScreenApp(sessionView: ISessionViewState): boolean {
    const fullscreenId = sessionView.sessionViewMetaData?.fullscreenId;
    if (!isFullScreeningAppInCall(sessionView) || !fullscreenId) {
      return false;
    }

    const appState = this.fullScreenAppManagerService.getAppState(fullscreenId);
    if (!appState || !this.fullScreenAppManagerService.isAppAvailable(fullscreenId)) {
      return false;
    }

    return !this.fullScreenAppManagerService.shouldHideAppFromCurrentUser(appState);
  }

  private shouldActivateGalleryCanvas(sessionView: ISessionViewState): boolean {
    return (
      (sessionView.view === SessionView.EXPANDED || sessionView.view === SessionView.FULLSCREEN) &&
      (sessionView.expandedFullScreenChange?.tileData?.trackType === TrackType.SCREEN ||
        this.canShowAnnotationsLayerOnFullScreenApp(sessionView))
    );
  }

  public resetToolbarStateIfEmpty(): void {
    if (this._toolbarState.lastSelectedTool !== BottomToolbarType.EMPTY) {
      return;
    }
    this._toolbarState = {
      lastSelectedTool: BottomToolbarType.PEN,
      lastActivePen: PenTypeEnum.DRAW,
    };
    this._selectedToolbarTool.next(this._toolbarState.lastSelectedTool);
  }

  public getScribbleType(): ScribbleType {
    if (this.toolbarState.lastSelectedTool !== BottomToolbarType.PEN) {
      return ScribbleType.NOT_SCRIBBLE;
    }
    return this.toolbarState.lastActivePen === PenTypeEnum.DRAW
      ? ScribbleType.DRAW
      : ScribbleType.HIGHLIGHT;
  }

  public toggleToolbarExpanded(expanded?: boolean): void {
    const newExpanded = expanded !== undefined ? expanded : !this._isToolbarExpanded.value;
    this._isToolbarExpanded.next(newExpanded);
  }

  public setCurrentVideoSource(videoSource: VideoSource): void {
    this._currentVideoSource = videoSource;
  }

  get currentVideoSource(): VideoSource {
    return this._currentVideoSource;
  }

  set toolbarState(state: ToolbarState) {
    this._toolbarState = state;
    this._selectedToolbarTool.next(state.lastSelectedTool);
  }

  get toolbarState(): ToolbarState {
    return this._toolbarState;
  }

  get isToolbarDisabled(): boolean {
    return this._isToolbarDisabled;
  }
}
